// Single source of truth for form embed snippets. Both the Forms-list Embed
// dialog (EmbedCodeModal) and the Form Builder Embed tab (FormBuilder) call this
// so the two dialogs always emit character-identical code.
//
// Both variants impose no width constraint. The host app (Elementor,
// WordPress, etc.) controls the iframe's width via its container. The
// iframe's height is set dynamically via postMessage auto-resize. Both
// variants start at height:0 with scrolling="no" to prevent any scrollbar
// flash before the first resize message arrives. The iframe variant
// includes a min-height:200px fallback so the form remains visible even if
// the resize listener script is stripped by a CMS sanitizer or blocked by
// CSP; the script variant does not need this because the iframe and listener
// are created atomically in the same script block.
//
// The script variant includes an idempotency guard (window.__nurtureFormInit)
// to prevent duplicate iframe creation when the script is re-executed
// (Elementor preview toggles, page caching, React strict-mode, etc.).

import { resolveDeviceMode } from '../../lib/deviceConstants';

/**
 * Sanitize a slug for safe interpolation into HTML/JS strings.
 * Matches the backend _make_slug / _sanitize_slug pattern: lowercase,
 * replace non-alphanumeric runs with hyphens, strip leading/trailing hyphens.
 * Defense-in-depth against XSS via custom admin-supplied slugs.
 */
export function sanitizeSlug(slug) {
    if (!slug) return '';
    return slug
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export function buildFormEmbedSnippet({ slug, origin, device } = {}) {
    if (!slug) return null;
    const safeSlug = sanitizeSlug(slug);
    if (!safeSlug) return null;
    const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
    const publicUrl = `${base}/form/${safeSlug}`;

    // Always include device param — no backward compatibility needed
    const deviceMode = resolveDeviceMode(device);
    const embedSrc = `${publicUrl}?embed=true&device=${deviceMode}`;

    // Shared listener script — used by BOTH variants.
    // Listens for nurture-form-resize postMessages and sets the iframe height.
    const listenerScript =
        `<script>\n` +
        `(function(){var f=document.getElementById('nurture-form-${safeSlug}');if(!f)return;` +
        `window.addEventListener('message',function(e){` +
        `if(e.data&&e.data.type==='nurture-form-resize'&&e.data.slug==='${safeSlug}')` +
        `{f.style.height=e.data.height+'px';}});})();\n` +
        `</script>`;

    // Plain iframe variant — includes the resize listener + idempotency-safe id.
    // Responsive sizing: width:100% in CSS style (not as HTML attribute) so the
    // iframe fills its container. Height starts at 0 and is set by the resize
    // listener. A min-height:200px fallback ensures the form remains visible
    // even if the listener script is stripped by a CMS sanitizer, blocked by
    // CSP, or not executed (e.g. inserted via innerHTML/dangerouslySetInnerHTML
    // which does not run <script> tags). scrolling="no" prevents scrollbar
    // flash. The host can override width:100% via CSS !important or by
    // constraining the container — this is a responsive default, not a fixed
    // constraint.
    const iframeCode =
        `<iframe id="nurture-form-${safeSlug}" src="${embedSrc}" ` +
        `style="border:none;width:100%;height:0;min-height:200px;" frameborder="0" scrolling="no"></iframe>\n` +
        listenerScript;

    // Script variant — same iframe + listener, but created dynamically.
    // Idempotency guard: window.__nurtureFormInit[slug] prevents duplicate
    // iframe creation when the script is re-executed.
    // The guard is SET after the container div existence check. If the guard
    // were set before the check and the container wasn't in the DOM yet (async
    // loading, <head> injection, CMS reordering), the guard would be poisoned
    // and all future re-executions would short-circuit, leaving the form
    // invisible with no recovery.
    const scriptCode =
        `<div id="nurture-form-${safeSlug}-container"></div>\n` +
        `<script>\n` +
        `(function(){\n` +
        `var d=document.getElementById('nurture-form-${safeSlug}-container');if(!d)return;\n` +
        `if(window.__nurtureFormInit&&window.__nurtureFormInit['${safeSlug}'])return;\n` +
        `window.__nurtureFormInit=window.__nurtureFormInit||{};\n` +
        `window.__nurtureFormInit['${safeSlug}']=true;\n` +
        `var f=document.createElement('iframe');\n` +
        `f.id='nurture-form-${safeSlug}';\n` +
        `f.src='${embedSrc}';\n` +
        `f.style.cssText='border:none;width:100%;height:0;';\n` +
        `f.setAttribute('frameborder','0');\n` +
        `f.setAttribute('scrolling','no');\n` +
        `d.appendChild(f);\n` +
        `window.addEventListener('message',function(e){` +
        `if(e.data&&e.data.type==='nurture-form-resize'&&e.data.slug==='${safeSlug}')` +
        `{f.style.height=e.data.height+'px';}});\n` +
        `})();\n` +
        `</script>`;

    return { publicUrl, embedSrc, iframeCode, scriptCode };
}
